import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, CheckCircle2, Loader2, Lock, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Kpi, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");
const words = (s) => String(s || "").replace(/_/g, " ");

const blank = () => ({ abstain: false, options: [], ranking: [] });

export const VotePaper = ({ electionRef, onChanged }) => {
  const [data, setData] = useState(null);
  const [marks, setMarks] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/elections/${electionRef}/voting`);
    setData(res.data);
    const next = {};
    res.data.ballot.sections.forEach((s) => { next[s.section_ref] = blank(); });
    (res.data.draft?.marks || []).forEach((m) => {
      next[m.section_ref] = { abstain: Boolean(m.abstain), options: m.options || [],
        ranking: m.ranking || [] };
    });
    setMarks(next);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const payload = () => ({
    marks: Object.entries(marks).map(([section_ref, m]) => ({ section_ref, ...m })),
  });

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(message);
      await load();
      onChanged?.();
      return res;
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
    return null;
  };

  if (!data) return <Panel title="Your ballot paper" testId="panel-vote-loading">
    <p className="text-[12.5px] text-muted-foreground">Opening your paper…</p></Panel>;

  const b = data.ballot;
  const open = data.voting_open;

  const setMark = (ref, next) => setMarks({ ...marks, [ref]: next });

  const toggleOption = (section, option) => {
    const m = marks[section.section_ref] || blank();
    if (section.paper_kind === "ranking") {
      const ranking = m.ranking.includes(option)
        ? m.ranking.filter((o) => o !== option) : [...m.ranking, option];
      setMark(section.section_ref, { abstain: false, options: [], ranking });
      return;
    }
    if (section.paper_kind === "multiple") {
      const chosen = m.options.includes(option)
        ? m.options.filter((o) => o !== option)
        : [...m.options, option].slice(0, section.choose_up_to);
      setMark(section.section_ref, { abstain: false, options: chosen, ranking: [] });
      return;
    }
    setMark(section.section_ref, { abstain: false,
      options: m.options[0] === option ? [] : [option], ranking: [] });
  };

  const move = (section, option, delta) => {
    const m = marks[section.section_ref] || blank();
    const at = m.ranking.indexOf(option);
    const to = at + delta;
    if (at < 0 || to < 0 || to >= m.ranking.length) return;
    const ranking = [...m.ranking];
    [ranking[at], ranking[to]] = [ranking[to], ranking[at]];
    setMark(section.section_ref, { ...m, ranking });
  };

  if (data.you_have_voted) {
    return (
      <Panel title="Your paper is in the box" testId="panel-voted"
             kicker="Cast and final — and untraceable to you">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-500" />
          <div>
            <p className="text-[13px] text-foreground">
              You voted in <span className="font-semibold">{data.election_name}</span> on{" "}
              {pretty(data.you_voted_at)}.
            </p>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Your confirmation reference is{" "}
              <span className="font-mono text-foreground" data-testid="vote-receipt">
                {data.your_receipt}
              </span>
              . It proves you voted and carries nothing about how you voted. A cast paper cannot be
              changed or withdrawn.
            </p>
            <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="voted-turnout">
              Turnout so far: {data.turnout.cast} of {data.turnout.roll} ({data.turnout.percent}%).
              No result is shown until the Commission counts and certifies it.
            </p>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="vote-kpi-state" label="Voting" icon={Lock}
             value={open ? "Open" : words(data.window_state)}
             sub={data.voting_window?.closes_at
               ? `closes ${pretty(data.voting_window.closes_at)}` : "window not set"} />
        <Kpi testId="vote-kpi-sections" label="Sections to mark" accent="violet"
             value={b.sections.length} sub={b.ballot_ref} />
        <Kpi testId="vote-kpi-draft" label="Private draft" accent="teal"
             value={data.draft?.saved_at ? "Saved" : "None"}
             sub={data.draft?.saved_at ? `kept until ${pretty(data.draft.expires_at)}`
               : "only you can ever see it"} />
        <Kpi testId="vote-kpi-turnout" label="Turnout" accent="emerald"
             value={`${data.turnout.percent}%`}
             sub={`${data.turnout.cast} of ${data.turnout.roll}`} />
      </div>

      <Panel title={b.title} testId="panel-vote-paper" kicker={b.instructions}>
        {!data.you_are_on_the_roll && (
          <p className="mb-3 text-[12px] text-amber-700 dark:text-amber-300"
             data-testid="vote-not-on-roll">
            You are not on the frozen roll for this election, so you may read the paper but not
            mark it.
          </p>
        )}
        <div className="space-y-3">
          {b.sections.map((s, i) => {
            const m = marks[s.section_ref] || blank();
            return (
              <div key={s.section_ref} className="rounded-xl border border-border p-3.5"
                   data-testid={`vote-section-${s.section_ref}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[13px] font-semibold text-foreground">{i + 1}. {s.title}</p>
                  <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    {s.paper_kind === "ranking" ? "rank them in order"
                      : s.paper_kind === "multiple" ? `choose up to ${s.choose_up_to}`
                        : "choose one"}
                  </span>
                </div>
                {s.detail && <p className="mt-1 text-[11.5px] text-muted-foreground">{s.detail}</p>}
                <div className="mt-2.5 space-y-1.5">
                  {s.options.map((o) => {
                    const rank = m.ranking.indexOf(o.option_ref);
                    const on = s.paper_kind === "ranking" ? rank >= 0
                      : m.options.includes(o.option_ref);
                    return (
                      <div key={o.option_ref} className="flex items-center gap-2">
                        <button type="button" disabled={!open || !data.you_are_on_the_roll}
                                data-testid={`choose-${o.option_ref}`}
                                onClick={() => toggleOption(s, o.option_ref)}
                                className={`flex flex-1 items-center justify-between rounded-xl border px-3 py-2 text-left text-[12.5px] transition-colors duration-150 ${
                                  on ? "border-primary/60 bg-primary/10 text-foreground"
                                    : "border-border text-muted-foreground hover:border-primary/30"} ${
                                  !open ? "opacity-60" : ""}`}>
                          <span>{o.label}</span>
                          {s.paper_kind === "ranking" && rank >= 0 && (
                            <span className="font-mono text-[11px] text-primary"
                                  data-testid={`rank-${o.option_ref}`}>
                              #{rank + 1}
                            </span>
                          )}
                          {s.paper_kind !== "ranking" && on && (
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                          )}
                        </button>
                        {s.paper_kind === "ranking" && rank >= 0 && (
                          <div className="flex flex-col gap-0.5">
                            <Button size="icon" variant="outline" className="h-7 w-8 rounded-lg"
                                    data-testid={`rank-up-${o.option_ref}`}
                                    onClick={() => move(s, o.option_ref, -1)}>
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="outline" className="h-7 w-8 rounded-lg"
                                    data-testid={`rank-down-${o.option_ref}`}
                                    onClick={() => move(s, o.option_ref, 1)}>
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {s.allow_abstain && (
                    <button type="button" disabled={!open || !data.you_are_on_the_roll}
                            data-testid={`abstain-${s.section_ref}`}
                            onClick={() => setMark(s.section_ref,
                              { abstain: !m.abstain, options: [], ranking: [] })}
                            className={`w-full rounded-xl border border-dashed px-3 py-2 text-left text-[12px] transition-colors duration-150 ${
                              m.abstain ? "border-primary/60 bg-primary/5 text-foreground"
                                : "border-border text-muted-foreground hover:border-primary/30"}`}>
                      I abstain on this section
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-9 rounded-full"
                  data-testid="save-draft" disabled={!open || !data.you_are_on_the_roll
                    || busy === "draft"}
                  onClick={() => act("draft",
                    () => api.put(`/elections/${electionRef}/vote-draft`, payload()),
                    "Saved privately — nobody else can see it, and you can change it any time")}>
            {busy === "draft" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Save className="mr-1.5 h-3.5 w-3.5" />}
            Save privately
          </Button>
          {data.draft?.saved_at && (
            <Button size="sm" variant="ghost" className="h-9 rounded-full"
                    data-testid="clear-draft"
                    onClick={() => act("clear",
                      () => api.delete(`/elections/${electionRef}/vote-draft`),
                      "Your private draft is gone")}>
              Start again
            </Button>
          )}
          <Button size="sm" className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="cast-vote" disabled={!open || !data.you_are_on_the_roll}
                  onClick={() => setConfirm(true)}>
            Cast my paper
          </Button>
          <Chip testId="secret-note" className="border-border bg-muted text-muted-foreground">
            secret ballot — your name is kept apart from your paper
          </Chip>
        </div>
      </Panel>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-lg" data-testid="cast-dialog">
          <DialogHeader><DialogTitle>Cast your paper?</DialogTitle>
            <DialogDescription>
              Casting is final. Your choices go into the box with no link back to you, and your
              name goes onto the marked register with no link to your choices. You cannot change
              or withdraw it afterwards.
            </DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" data-testid="cast-cancel"
                    onClick={() => setConfirm(false)}>Keep marking</Button>
            <Button data-testid="cast-confirm" disabled={busy === "cast"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={async () => {
                      const res = await act("cast",
                        () => api.post(`/elections/${electionRef}/cast`, payload()),
                        "Your paper is in the box");
                      if (res) setConfirm(false);
                    }}>
              {busy === "cast" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cast it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
